import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import validate from "validate.js";
import { showSnackbar } from "../../app/slices/commonSlice";
import {
  addNewUser,
  resetStateUsers,
  updateUser,
} from "../../app/slices/userSlice";
import { getObjectDifference, hasAccess, roles } from "../../app/utils";
import React from "react";

const UpdateUserDialog = ({ open, handleClose, selectedUser }) => {
  const {
    put: { data: dataPutUser, status: statusPutUser, error: errorPutUser },
    post: { data: dataPostUser, status: statusPostUser, error: errorPostUser },
  } = useSelector(({ users }) => users);

  const dispatch = useDispatch();

  const [isReadOnly] = useState(
    !hasAccess({ type: "name", resource: "EDIT USERS" }),
  );

  const [shouldRefresh, setShouldRefresh] = useState(false);

  const [formValues, setFormValues] = useState({
    firstName: selectedUser?.FIRST_NAME || "",
    middleName: selectedUser?.MIDDLE_NAME || "",
    lastName: selectedUser?.LAST_NAME || "",
    email: selectedUser?.EMAIL || "",
    phoneNumber: selectedUser?.PHONE_NUMBER || "",
    role: selectedUser?.ROLE_ID || "",
    status: selectedUser?.STATUS || "A",
  });

  const [errors, setErrors] = useState({});

  const hasChanges = React.useMemo(() => {
    if (!selectedUser) return true;
    const currentPayload = {
      EMAIL: formValues.email.toLowerCase(),
      ROLE_ID: formValues.role,
      FIRST_NAME: formValues.firstName.toUpperCase(),
      MIDDLE_NAME: formValues.middleName.toUpperCase(),
      LAST_NAME: formValues.lastName.toUpperCase(),
      PHONE_NUMBER: formValues.phoneNumber,
      STATUS: formValues.status,
    };
    return (
      Object.keys(getObjectDifference(selectedUser, currentPayload)).length > 0
    );
  }, [formValues, selectedUser]);

  const statuses = [
    { value: "A", label: "Active" },
    { value: "I", label: "Inactive" },
    { value: "D", label: "Disabled" },
  ];

  const validateSchema = {
    firstName: { presence: { allowEmpty: false } },
    middleName: { presence: { allowEmpty: false } },
    lastName: { presence: { allowEmpty: false } },
    role: { presence: { allowEmpty: false } },
    email: { presence: { allowEmpty: false }, email: true },
    phoneNumber: { presence: { allowEmpty: false }, length: { minimum: 10 } },
  };

  useEffect(() => {
    if (dataPutUser || dataPostUser) {
      dispatch(
        showSnackbar({
          open: true,
          type: "success",
          message: `User ${
            dataPutUser ? "updated" : "registered"
          } successfully.`,
        }),
      );

      setShouldRefresh(true);
      dispatch(resetStateUsers());
      handleClose(true);
    }
  }, [dataPutUser, dataPostUser, dispatch, handleClose]);

  useEffect(() => {
    if (errorPutUser || errorPostUser) {
      dispatch(
        showSnackbar({
          open: true,
          type: "error",
          message:
            errorPutUser?.message ||
            errorPostUser?.message ||
            "An error occurred",
        }),
      );

      dispatch(resetStateUsers(["put", "post"]));
    }
  }, [errorPutUser, errorPostUser, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      if (!/^[0-9]*$/.test(value)) return;
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedUser?.STATUS === "D") {
      dispatch(
        showSnackbar({
          open: true,
          message: "User is disabled. Update is not allowed",
          type: "error",
        }),
      );
      return;
    }

    if (!hasChanges) {
      dispatch(
        showSnackbar({
          open: true,
          message: "No changes to submit",
          type: "info",
        }),
      );
      return;
    }

    const validationErrors = validate(formValues, validateSchema);

    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    const payload = {
      EMAIL: formValues.email.toLowerCase(),
      ROLE_ID: formValues.role,
      FIRST_NAME: formValues.firstName.toUpperCase(),
      MIDDLE_NAME: formValues.middleName.toUpperCase(),
      LAST_NAME: formValues.lastName.toUpperCase(),
      PHONE_NUMBER: formValues.phoneNumber,
      STATUS: formValues.status,
    };

    if (selectedUser) {
      const diff = getObjectDifference(selectedUser, payload);

      dispatch(
        updateUser({
          id: selectedUser.USER_ID,
          payload: diff,
        }),
      );
    } else {
      delete payload.STATUS;
      dispatch(addNewUser(payload));
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      onClose={() => handleClose(shouldRefresh)}
    >
      <DialogTitle sx={{ fontWeight: "bold" }}>
        {selectedUser ? "Update" : "Register"} User
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formValues.firstName}
                onChange={handleChange}
                disabled={isReadOnly}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName?.[0] || ""}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Middle Name"
                name="middleName"
                value={formValues.middleName}
                onChange={handleChange}
                disabled={isReadOnly}
                error={Boolean(errors.middleName)}
                helperText={errors.middleName?.[0] || ""}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formValues.lastName}
                onChange={handleChange}
                disabled={isReadOnly}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName?.[0] || ""}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formValues.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email?.[0] || ""}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Username"
                value={formValues.email?.split("@")[0] || ""}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                select
                fullWidth
                label="User Role"
                name="role"
                value={formValues.role}
                onChange={handleChange}
                disabled={isReadOnly}
              >
                {roles.map((option) => (
                  <MenuItem key={option.roleId} value={option.roleId}>
                    {option.roleName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Mobile Phone"
                name="phoneNumber"
                value={formValues.phoneNumber}
                onChange={handleChange}
                disabled={isReadOnly}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber?.[0] || ""}
              />
            </Grid>

            {selectedUser && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  label="User Status"
                  name="status"
                  value={formValues.status}
                  onChange={handleChange}
                  disabled={isReadOnly}
                >
                  {statuses.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>

          <DialogActions sx={{ mt: 3, px: 0 }}>
            {!isReadOnly && !shouldRefresh && (
              <Button
                type="submit"
                variant="contained"
                disabled={
                  statusPutUser === "loading" ||
                  statusPostUser === "loading" ||
                  !hasChanges
                }
              >
                {(statusPutUser === "loading" ||
                  statusPostUser === "loading") && (
                  <CircularProgress size={18} sx={{ mr: 1 }} />
                )}
                {selectedUser ? "Update" : "Register"}
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={() => handleClose(shouldRefresh)}
            >
              Close
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateUserDialog;
